import React, { useReducer } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { arrayMove } from '@dnd-kit/sortable';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import ToolPage from './pages/ToolPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactUsPage from './pages/ContactUsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';

import './App.css';

if (process.env.NODE_ENV === 'development') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;
} else {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
}

const initialState = {
  files: [],
  thumbnails: {},
  loading: false,
  loadingThumbnails: false,
  pageCounts: {},
  splitRanges: '',
  compressionQuality: 80,
  rotationAngle: 90,
  password: '',
  watermarkText: '',
  watermarkOpacity: 0.5,
  formFields: [],
  formData: {},
  message: '',
  messageType: '',
  resultUrl: '',
  resultFilename: '',
  splitResult: [],
  convertedImagesResult: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LOADING_THUMBNAILS':
      return { ...state, loadingThumbnails: action.payload };
    case 'ADD_FILES':
      return { ...state, files: [...state.files, ...action.payload] };
    case 'REMOVE_FILE':
      return { ...state, files: state.files.filter(f => f.id !== action.payload) };
    case 'UPDATE_THUMBNAILS_AND_PAGE_COUNTS':
      return {
        ...state,
        thumbnails: { ...state.thumbnails, ...action.payload.thumbnails },
        pageCounts: { ...state.pageCounts, ...action.payload.pageCounts },
      };
    case 'SET_MESSAGE':
      return { ...state, message: action.payload.message, messageType: action.payload.type };
    case 'SET_RESULT_URL':
      return { ...state, resultUrl: action.payload };
    case 'SET_RESULT_FILENAME':
      return { ...state, resultFilename: action.payload };
    case 'SET_SPLIT_RESULT':
      return { ...state, splitResult: action.payload };
    case 'SET_CONVERTED_IMAGES_RESULT':
      return { ...state, convertedImagesResult: action.payload };
    case 'REORDER_FILES':
      return { ...state, files: action.payload };
    case 'SET_TOOL_OPTION':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'CLEAR_STATE':
      // Reset results and files, but keep tool-specific options
      return {
        ...state,
        files: [],
        thumbnails: {},
        pageCounts: {},
        message: '',
        messageType: '',
        resultUrl: '',
        resultFilename: '',
        splitResult: [],
        convertedImagesResult: [],
      };
    case 'CLEAR_RESULTS':
      return {
        ...state,
        message: '',
        messageType: '',
        resultUrl: '',
        resultFilename: '',
        splitResult: [],
        convertedImagesResult: [],
      };
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

const App = () => {
  const { i18n } = useTranslation();

  // The public URL may have a trailing slash, but the router's `basename`
  // should not. This makes the routing work correctly in a sub-directory.
  const basename = "/";

  return (
    <Router basename={basename}>
      <Main />
    </Router>
  );
};

const Main = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    files, thumbnails, loading, loadingThumbnails, pageCounts, splitRanges,
    compressionQuality, rotationAngle, password, watermarkText, watermarkOpacity,
    formFields, formData, message, messageType, resultUrl, resultFilename, splitResult,
    convertedImagesResult
  } = state;
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;

  const handleToolSelect = (tool) => {
    console.log('handleToolSelect called');
    handleClear();
    navigate(`/tool/${tool}`);
  };
  const handleGoHome = () => {
    console.log('handleGoHome called');
    handleClear();
    navigate('/');
  };
  const handleClear = () => {
    console.log('handleClear called');
    dispatch({ type: 'CLEAR_STATE' });
  };

  const handleFileDownload = (response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch.length > 1) {
        filename = filenameMatch[1];
      }
    }
    dispatch({ type: 'SET_RESULT_URL', payload: url });
    dispatch({ type: 'SET_RESULT_FILENAME', payload: filename });
  }

  const onDrop = async (acceptedFiles) => {
    console.log('onDrop called');
    dispatch({ type: 'CLEAR_RESULTS' });
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_LOADING_THUMBNAILS', payload: true });

    const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');

    const newFiles = pdfFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      order: files.length + 1,
    }));

    if (newFiles.length > 0) {
      console.log('Adding new files', newFiles);
      dispatch({ type: 'ADD_FILES', payload: newFiles });

      const newThumbnails = {};
      const newPageCounts = {};

      let errorOccurred = false;

      for (const newFile of newFiles) {
        try {
          const arrayBuffer = await newFile.file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          newPageCounts[newFile.id] = pdf.numPages;

          if (pdf.numPages > 0) {
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 0.5 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            await page.render({ canvasContext: context, viewport }).promise;
            newThumbnails[newFile.id] = canvas.toDataURL();
          }
        } catch (error) {
          console.error(`Error generating thumbnail for ${newFile.file.name}:`, error);
          errorOccurred = true;
        }
      }

      if (errorOccurred) {
        dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.thumbnailError'), type: 'error' } });
      }

      dispatch({
        type: 'UPDATE_THUMBNAILS_AND_PAGE_COUNTS',
        payload: { thumbnails: newThumbnails, pageCounts: newPageCounts },
      });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
    dispatch({ type: 'SET_LOADING_THUMBNAILS', payload: false });
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({ onDrop, noClick: true, accept: 'application/pdf' });
  const removeFile = (id) => dispatch({ type: 'REMOVE_FILE', payload: id });

  
  
  

  const handleOnDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = files.findIndex((file) => file.id === active.id);
      const newIndex = files.findIndex((file) => file.id === over.id);

      dispatch({ type: 'REORDER_FILES', payload: arrayMove(files, oldIndex, newIndex) });
    }
  };

  const handleMerge = async () => {
    if (files.length < 2) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.minFiles'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    files.forEach(f => fd.append('pdfs', f.file));
    try {
      const response = await axios.post(`${API_URL}/merge`, fd, { responseType: 'blob' });
      console.log('Response received (frontend):', response);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      console.log('Generated Blob URL (frontend):', url);
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.mergeSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error merging PDFs:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.mergeError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleSplit = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('pdf', files[0].file);
    fd.append('ranges', JSON.stringify(splitRanges.split(',').map(s => s.trim())));
    try {
      const response = await axios.post(`${API_URL}/split`, fd);
      const updatedSplitResult = response.data.splitPdfs.map(file => ({
        ...file,
        url: `${API_URL}/download/${file.filename}`
      }));
      dispatch({ type: 'SET_SPLIT_RESULT', payload: updatedSplitResult });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.splitSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error splitting PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.splitError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleCompress = async () => {
    if (files.length === 0) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.noFiles'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    files.forEach(f => fd.append('files', f.file));
    fd.append('quality', compressionQuality);
    try {
      const response = await axios.post(`${API_URL}/compress`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.compressSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error compressing PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.connection'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleConvertToPDF = async () => {
    if (files.length === 0) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.noFiles'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    files.forEach(f => fd.append('files', f.file));
    try {
      const response = await axios.post(`${API_URL}/convert-to-pdf`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.convertSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error converting to PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.convertError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleConvertToImages = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('file', files[0].file);
    try {
      const response = await axios.post(`${API_URL}/convert-to-images`, fd);
      dispatch({ type: 'SET_CONVERTED_IMAGES_RESULT', payload: response.data.files });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.convertSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error converting to images:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.convertError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleRotate = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('pdf', files[0].file);
    fd.append('angle', rotationAngle);
    try {
      const response = await axios.post(`${API_URL}/rotate`, fd, { responseType: 'blob' });
      handleFileDownload(response);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.rotateSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error rotating PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.rotateError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleOrganize = async () => {
    if (files.length === 0) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.noFiles'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    const fileOrder = files.map(f => f.file.name);
    files.forEach(f => fd.append('files', f.file));
    fd.append('order', JSON.stringify(fileOrder));
    try {
      const response = await axios.post(`${API_URL}/organize`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.mergeSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error organizing PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.mergeError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleProtect = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('file', files[0].file);
    fd.append('password', password);
    try {
      const response = await axios.post(`${API_URL}/protect`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.passwordSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error protecting PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.passwordError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleUnlock = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('file', files[0].file);
    fd.append('password', password);
    try {
      const response = await axios.post(`${API_URL}/unlock`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.passwordSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error unlocking PDF:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.passwordError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleAddWatermark = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('pdf', files[0].file);
    fd.append('text', watermarkText);
    fd.append('opacity', watermarkOpacity);
    try {
      const response = await axios.post(`${API_URL}/watermark`, fd, { responseType: 'blob' });
      handleFileDownload(response);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.watermarkSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error adding watermark:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.watermarkError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleAddPageNumbers = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('pdf', files[0].file);
    try {
      const response = await axios.post(`${API_URL}/page-numbers`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.pageNumbersSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error adding page numbers:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.pageNumbersError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleExtractText = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('pdf', files[0].file);
    try {
      const response = await axios.post(`${API_URL}/extract-text`, fd, { responseType: 'blob' });
      handleFileDownload(response);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.extractSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error extracting text:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.extractError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleFillForm = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('file', files[0].file);
    fd.append('fields', JSON.stringify(formData));
    try {
      const response = await axios.post(`${API_URL}/fill-form`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.formFillSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error filling form:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.formFillError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleConvertToWord = async () => {
    if (files.length !== 1) {
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.error.oneFileRequired'), type: 'error' } });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const fd = new FormData();
    fd.append('pdf', files[0].file);
    try {
      const response = await axios.post(`${API_URL}/convert-to-word`, fd, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      dispatch({ type: 'SET_RESULT_URL', payload: url });
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.convertSuccess'), type: 'success' } });
    } catch (error) {
      console.error('Error converting to Word:', error);
      dispatch({ type: 'SET_MESSAGE', payload: { message: t('messages.convertError'), type: 'error' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handlers = {
    merge: handleMerge,
    split: handleSplit,
    compress: handleCompress,
    'convert-to-pdf': handleConvertToPDF,
    'convert-to-images': handleConvertToImages,
    rotate: handleRotate,
    organize: handleOrganize,
    protect: handleProtect,
    unlock: handleUnlock,
    watermark: handleAddWatermark,
    'extract-text': handleExtractText,
    'fill-form': handleFillForm,
    'convert-to-word': handleConvertToWord,
    'page-numbers': handleAddPageNumbers,
  };

  return (
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage onToolSelect={handleToolSelect} />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route
            path="/tool/:tool"
            element={
              <ToolPage
                key={resultUrl || message || (splitResult && splitResult.length > 0)} // Add key to force re-render
                onDrop={onDrop}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isDragActive={isDragActive}
                handleFileSelect={open}
                loading={loading}
                files={files}
                thumbnails={thumbnails}
                loadingThumbnails={loadingThumbnails}
                removeFile={removeFile}
                pageCounts={pageCounts}
                splitRanges={splitRanges}
                setSplitRanges={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'splitRanges', value } })}
                compressionQuality={compressionQuality}
                setCompressionQuality={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'compressionQuality', value } })}
                rotationAngle={rotationAngle}
                setRotationAngle={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'rotationAngle', value } })}
                password={password}
                setPassword={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'password', value } })}
                watermarkText={watermarkText}
                setWatermarkText={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'watermarkText', value } })}
                watermarkOpacity={watermarkOpacity}
                setWatermarkOpacity={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'watermarkOpacity', value } })}
                formFields={formFields}
                formData={formData}
                setFormData={(value) => dispatch({ type: 'SET_TOOL_OPTION', payload: { key: 'formData', value } })}
                handleFillForm={handleFillForm}
                handleConvertToWord={handleConvertToWord}
                handlers={handlers}
                mergeMessage={message}
                mergeMessageType={messageType}
                resultUrl={resultUrl}
                resultFilename={resultFilename}
                splitResult={splitResult}
                convertedImagesResult={convertedImagesResult}
                handleClear={handleClear}
                handleGoHome={handleGoHome}
                handleOnDragEnd={handleOnDragEnd}
              />
            }
          />
        </Routes>
      </Layout>
  );
};

export default App;
